/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Shared react-select control style builders.
 * @module utils/reactSelectControlStyles
 */

import { hideSelectIndicatorSeparator } from "./reactSelectFieldUtils.jsx";

const DEFAULT_BORDER_COLOR = "#909092";
const DEFAULT_BACKGROUND = "#FCFCFC";
const COMPACT_MENU_MAX_HEIGHT = "110px";
const TALL_MENU_MAX_HEIGHT = "200px";

const passThroughStyle = (baseStyles) => ({ ...baseStyles });

/**
 * Scrollable menu and menu-list styles shared by filter dropdowns.
 * @param {string} maxHeight
 * @returns {{ menu: Function, menuList: Function }}
 */
const buildScrollableMenuStyles = (maxHeight) => ({
  menu: (baseStyles) => ({
    ...baseStyles,
    maxHeight,
    overflowY: "auto",
  }),
  menuList: (baseStyles) => ({
    ...baseStyles,
    maxHeight,
    overflowY: "auto",
    padding: "4px 0",
  }),
});

/**
 * Standard full-width filter select used by reports, notifications, and rooms.
 * Menu height is capped so long option lists stay inside the filter row.
 */
export const compactSelectStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "8px",
    width: "100%",
    height: "40px",
    fontSize: "16px",
    cursor: "pointer",
    textAlign: "left",
    borderColor: DEFAULT_BORDER_COLOR,
  }),
  container: (baseStyles) => ({
    ...baseStyles,
    width: "100%",
  }),
  valueContainer: passThroughStyle,
  input: passThroughStyle,
  option: (baseStyles) => ({
    ...baseStyles,
    cursor: "pointer",
  }),
  ...buildScrollableMenuStyles(COMPACT_MENU_MAX_HEIGHT),
  indicatorSeparator: hideSelectIndicatorSeparator,
};

/** Same control as {@link compactSelectStyles}, with a taller options menu. */
export const tallMenuSelectStyles = {
  ...compactSelectStyles,
  ...buildScrollableMenuStyles(TALL_MENU_MAX_HEIGHT),
};

/**
 * Multi-company picker: keep selected values on one line and use a taller menu.
 */
export const multiCompanySelectStyles = {
  ...compactSelectStyles,
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  option: (baseStyles) => ({
    ...baseStyles,
    textAlign: "left",
    cursor: "pointer",
  }),
  ...buildScrollableMenuStyles(TALL_MENU_MAX_HEIGHT),
};

/**
 * Multi-value event-type picker.
 * minHeight keeps the closed control at 40px; values stay on one line.
 */
export const multiEventTypeSelectStyles = {
  ...compactSelectStyles,
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "8px",
    width: "100%",
    minHeight: "40px",
    fontSize: "16px",
    cursor: "pointer",
    textAlign: "left",
    borderColor: DEFAULT_BORDER_COLOR,
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
};

/**
 * Base react-select control styles for full-width company pickers.
 * @returns {Object}
 */
export const buildFullWidthSelectStyles = () => ({
  control: (base) => ({
    ...base,
    borderRadius: "8px",
    width: "100%",
    minHeight: "40px",
    fontSize: "16px",
    cursor: "pointer",
    textAlign: "left",
    borderColor: DEFAULT_BORDER_COLOR,
  }),
  container: (base) => ({ ...base, width: "100%" }),
  indicatorSeparator: hideSelectIndicatorSeparator,
});
